<script lang="ts">
import { defineComponent } from 'vue'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import type ResponseData from '@/types/ResponseData'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import * as _ from 'lodash'

export default defineComponent({
  name: 'company-table',
  props: ['hladaj'],
  data() {
    return {
      companies: [] as Company[],
      loaded: false,
      pagination: {} as any,
      selectedColumn: 'id',
      selectedDirection: 'desc' as 'asc' | 'desc',
      searchTerm: '',
      pagesRange: 5
    }
  },
  methods: {
    sortBy(column: string) {
      if (this.selectedColumn === column) {
        this.selectedDirection = this.selectedDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.selectedColumn = column;
        this.selectedDirection = 'asc';
      }
      this.retrieveCompanies(1);
    },
    refreshList() {
      this.retrieveCompanies(1);
    },
    retrieveCompanies(page: number) {
      if (this.searchTerm) {
        const id = Number(this.searchTerm);
        if (Number.isInteger(id) && id > 0) {
          CompanyDataService.getCompanyById(id)
            .then((response: ResponseData) => {
              this.companies = [response.data.data];
              this.pagination = { current_page: 1, last_page: 1, total: 1 };
              this.loaded = true;
            })
            .catch((e: Error) => {
              this.companies = [];
              this.pagination = { current_page: 1, last_page: 1, total: 0 };
              this.loaded = true;
              if (e.message !== 'Request failed with status code 404') {
                toast.error('Chyba: ' + e.message);
              }
            });
        } else {
          CompanyDataService.searchCompanies(this.searchTerm)
            .then((response: ResponseData) => {
              this.companies = response.data.data.data;
              this.pagination = response.data.data;
              this.loaded = true;
            })
            .catch((e: Error) => {
              this.loaded = true;
              toast.error('Chyba: ' + e.message);
            });
        }
      } else if (this.hladaj != null && this.hladaj != 'all' && this.hladaj != '') {
        CompanyDataService.getAllCompaniesForUser(this.hladaj)
          .then((response: ResponseData) => {
            this.companies = response.data.data.data;
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
          })
      } else {
        CompanyDataService.getAllCompanies(page, this.selectedColumn, this.selectedDirection)
          .then((response: ResponseData) => {
            this.companies = response.data.data.data;
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            this.loaded = true;
            toast.error('Chyba: ' + e.message);
          });
      }
    },
  },
  computed: {
    pages() {
      const currentPage = this.pagination.current_page;
      const lastPage = this.pagination.last_page;
      const startPage = Math.max(1, currentPage - Math.floor(this.pagesRange / 2));
      const endPage = Math.min(lastPage, startPage + this.pagesRange - 1);

      return _.range(startPage, endPage + 1);
    }
  },
  watch: {
    searchTerm() {
      this.retrieveCompanies(1);
    }
  },
  mounted() {
    this.retrieveCompanies(1);
  },
})

</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div class="py-4">
          <div class="flex flex-col">
            <div class="overflow-x-auto">
              <div class="flex flex-row justify-between">
                <div class="relative max-w-xs">
                  <label for="search" class="sr-only"> Hľadať </label>
                  <FormKit v-model="searchTerm" type="search" name="search"
                    class="block w-full p-3 pl-10 text-sm border-gray-200 rounded-md focus:border-teal-500 focus:ring-teal-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Hľadať..." />
                </div>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="!loaded">
                <div class="text-center">
                  <div role="status">
                    <svg class="inline mr-2 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-teal-600"
                      viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                    </svg>
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
              <div class="columns" v-if="loaded && !companies?.length">
                <p>No items found</p>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="loaded && companies?.length">
                <div class="overflow-hidden border rounded-lg">
<table class="min-w-full divide-y divide-gray-200">
                     <thead class="bg-gray-50">
                       <tr>
                         <th scope="col"
                           class="flex items-center px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                           <span class="inline-flex items-center cursor-pointer" :class="{ 'text-teal-500': selectedColumn == 'id' }" @click="sortBy('id')">
                             ID
                             <button>
                               <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-3 h-3" aria-hidden="true"
                                 fill="currentColor" viewBox="0 0 320 512">
                                 <path
                                   d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                               </svg>
                             </button>
                           </span>
                         </th>
                         <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                           <span class="inline-flex items-center cursor-pointer" :class="{ 'text-teal-500': selectedColumn == 'name' }" @click="sortBy('name')">
                             Názov
                             <button>
                               <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-3 h-3" aria-hidden="true"
                                 fill="currentColor" viewBox="0 0 320 512">
                                 <path
                                   d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                               </svg></button></span>
                         </th>
                         <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                           <span class="inline-flex items-center cursor-pointer"
                             :class="{ 'text-teal-500': selectedColumn == 'owner' }" @click="sortBy('owner')">
                             Zakladateľ
                             <button>
                               <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-3 h-3" aria-hidden="true"
                                 fill="currentColor" viewBox="0 0 320 512">
                                 <path
                                   d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                               </svg></button></span>
                         </th>
                         <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                           <span class="inline-flex items-center cursor-pointer"
                             :class="{ 'text-teal-500': selectedColumn == 'status' }" @click="sortBy('status')">
                             Stav
                             <button>
                               <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-3 h-3" aria-hidden="true"
                                 fill="currentColor" viewBox="0 0 320 512">
                                 <path
                                   d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                               </svg></button></span>
                         </th>
                         <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                           <span class="inline-flex items-center cursor-pointer"
                             :class="{ 'text-teal-500': selectedColumn == 'virtual' }" @click="sortBy('virtual')">
                             Je sídlo virtuálne?
                             <button>
                               <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-3 h-3" aria-hidden="true"
                                 fill="currentColor" viewBox="0 0 320 512">
                                 <path
                                   d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                               </svg></button></span>
                         </th>
                         <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                           <span class="inline-flex items-center cursor-pointer" :class="{ 'text-teal-500': selectedColumn == 'date' }" @click="sortBy('updated_at')">
                             Dátum zmeny
                             <button>
                               <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-3 h-3" aria-hidden="true"
                                 fill="currentColor" viewBox="0 0 320 512">
                                 <path
                                   d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                               </svg>
                             </button>
                           </span>
                         </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-right text-gray-500 uppercase">Edit</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                      <tr class="hover:bg-gray-200" :class="[company.is_virtual ? 'bg-gray-100' : 'bg-white']"
                        v-for="company in companies" :key="company.id">
                        <td class="px-6 py-4 text-sm font-medium text-gray-800">{{ company.id }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ company.name }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">
                          {{ company.first_name }} {{ company.last_name ? company.last_name : company.owner }}
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ company.status }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">
                          <input id="is_virtual" disabled :checked="company.is_virtual" type="checkbox"
                            class="w-4 h-4 focus:ring-teal-500 focus:border-teal-500 block shadow-sm sm:text-sm border-gray-300 rounded-md text-teal-500" />
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ company.updated_at }}</td>
                        <td class="px-6 py-4 text-sm font-medium text-right">
                          <router-link :to="'/company-detail/' + company.id"
                            class="text-green-500 hover:text-green-700">Edit</router-link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div class="flex flex-1 justify-between sm:hidden">
                      <a href="#"
                        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Previous</a>
                      <a href="#"
                        class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Next</a>
                    </div>
                    <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p class="text-sm text-gray-700">
                          Stránka
                          <span class="font-medium">{{ pagination.current_page }}</span>
                          z
                          <span class="font-medium">{{ pagination.last_page }}</span>
                        </p>
                      </div>
                      <div>
                        <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
<button
                             class="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                             :disabled="pagination.current_page <= 1"
                             @click="retrieveCompanies(pagination.current_page - 1)">
                             <span class="sr-only">Previous</span>
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>

                          <!-- Dynamicky generované čísla stránok -->
                          <button v-for="pageNumber in pages" :key="pageNumber"
                            @click="retrieveCompanies(pageNumber)" :class="{
                              'z-10 bg-teal-50 border-teal-500 text-teal-600': pageNumber === pagination.current_page,
                              'border-gray-300 hover:bg-gray-50 focus:z-20': pageNumber !== pagination.current_page
                            }" class="relative inline-flex items-center border text-gray-500 hover:bg-gray-50 bg-white px-4 py-2 text-sm font-medium">
                            {{ pageNumber }}
                          </button>

                          <button
                            class="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                            :disabled="pagination.current_page >= pagination.last_page"
                            @click="retrieveCompanies(pagination.current_page + 1)">
                            <span class="sr-only">Next</span>
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
